// src/utils/curriculumAreaScope.ts
//
// Decides which grid classes a curriculum leader is allowed to see.
//
// Three surfaces used to answer this question with their own hand-rolled
// substring cascades (Teacher Assignments, Read-Only Grids, and the read-only
// grid viewer modal). They disagreed with each other and, more importantly,
// they compared two DIFFERENT vocabularies as if they were one:
//
//   * a leader's `staff.curriculum_area` is normalised to the staff vocabulary
//     ("Health & PE") by 20260811_normalise_staff_learning_areas.sql, whereas
//   * a class's `curriculumArea` inside `grids.classes` is normalised to the
//     grid vocabulary ("HPE") by the `convert_pe_to_hpe` trigger on `grids`.
//
// "health & pe" contains neither "hpe" nor any of the other HPE spellings the
// old cascade looked for, and "hpe" does not contain "health & pe", so the HPE
// leader matched nothing at all and saw an empty screen (issue #187).
//
// The fix is to stop comparing raw strings: run BOTH sides through
// `normalizeLearningArea` first, which is the single source of truth for
// folding every observed spelling onto one canonical learning-area name, then
// compare canonical to canonical.

import { normalizeLearningArea } from '../constants/learningAreas';

/** The one learning area whose leader also owns VET delivery at this school. */
const VOCATIONAL_MAJOR = 'Vocational Major';

function lower(value: unknown): string {
  return String(value ?? '').toLowerCase();
}

/**
 * True when `gridName` is the Wednesday VET grid, every class of which belongs
 * to the Vocational Major leader regardless of the learning area recorded on
 * the individual classes.
 */
function isWednesdayVETGrid(gridName?: string | null): boolean {
  const name = lower(gridName);
  return name.includes('wednesday') && name.includes('vet');
}

/**
 * VET classes are delivered across several learning areas ("VET SPORT &
 * RECREATION" is filed under HPE, "VET MUSIC - PERFORMANCE" under Arts) but the
 * Vocational Major leader is responsible for all of them. This preserves the
 * extra reach the Vocational Major leader has always had.
 */
function isVocationalDelivery(cls: any): boolean {
  const haystack = `${lower(cls?.className)} ${lower(cls?.department)}`;
  return (
    haystack.includes('vet') ||
    haystack.includes('vocational') ||
    /\bvm\b/.test(haystack)
  );
}

/**
 * The canonical learning area a grid class belongs to. Falls back to the class's
 * department only when no curriculum area was recorded on the class itself.
 */
export function classLearningArea(cls: any): string | null {
  return (
    normalizeLearningArea(cls?.curriculumArea) ??
    normalizeLearningArea(cls?.department)
  );
}

/**
 * True when a curriculum leader responsible for `leaderArea` may see `cls`.
 *
 * `gridName` is optional and only affects the Vocational Major leader, who sees
 * the whole Wednesday VET grid.
 */
export function classMatchesLeaderArea(
  cls: any,
  leaderArea: string | null | undefined,
  gridName?: string | null
): boolean {
  if (!leaderArea) return false;

  const leaderCanonical = normalizeLearningArea(leaderArea);

  // A leader whose area is not a recognised learning area (a bespoke or
  // mistyped value) keeps the old literal comparison rather than losing access.
  if (!leaderCanonical) {
    const leaderLower = lower(leaderArea).trim();
    const classLower = lower(cls?.curriculumArea).trim();
    return (
      !!classLower &&
      (classLower === leaderLower ||
        classLower.includes(leaderLower) ||
        leaderLower.includes(classLower))
    );
  }

  if (leaderCanonical === VOCATIONAL_MAJOR) {
    if (isWednesdayVETGrid(gridName)) return true;
    if (isVocationalDelivery(cls)) return true;
  }

  return classLearningArea(cls) === leaderCanonical;
}

/** `classMatchesLeaderArea` applied across a grid's class list. */
export function filterClassesForLeaderArea<T>(
  classes: T[] | null | undefined,
  leaderArea: string | null | undefined,
  gridName?: string | null
): T[] {
  if (!classes) return [];
  if (!leaderArea) return classes;
  return classes.filter((cls) => classMatchesLeaderArea(cls, leaderArea, gridName));
}
